import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { API_CONFIG } from '@/config/api';
import { wsApi } from '@/services/api';

const MindWebSocketContext = createContext(null);

// Connection states
const WS_STATE = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
};

// Reconnection config
const MAX_RECONNECT_ATTEMPTS = 8;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30000;

export function MindWebSocketProvider({ children }) {
  const [connectionState, setConnectionState] = useState(WS_STATE.DISCONNECTED);
  const [lastMessage, setLastMessage] = useState(null);

  // Refs for stable references across renders
  const wsRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const listenersRef = useRef(new Map()); // messageType -> Set<callback>
  const activeRef = useRef(false); // true while intentionally connected
  const activeOptionsRef = useRef({});

  /**
   * Subscribe to a specific message type.
   * Returns an unsubscribe function.
   *
   * Supported types: 'presence_update', 'new_thought', 'system_alert', '*' (wildcard)
   */
  const subscribe = useCallback((messageType, callback) => {
    if (!listenersRef.current.has(messageType)) {
      listenersRef.current.set(messageType, new Set());
    }
    listenersRef.current.get(messageType).add(callback);

    // Return unsubscribe function
    return () => {
      listenersRef.current.get(messageType)?.delete(callback);
    };
  }, []);

  /**
   * Dispatch a received message to all subscribers of its type.
   */
  const dispatch = useCallback((message) => {
    setLastMessage(message);

    // Dispatch to specific type listeners
    const typeListeners = listenersRef.current.get(message.type);
    if (typeListeners) {
      typeListeners.forEach((cb) => {
        try {
          cb(message);
        } catch (err) {
          console.error('[MindWS] Subscriber error:', err);
        }
      });
    }

    // Dispatch to wildcard listeners
    const wildcardListeners = listenersRef.current.get('*');
    if (wildcardListeners) {
      wildcardListeners.forEach((cb) => {
        try {
          cb(message);
        } catch (err) {
          console.error('[MindWS] Wildcard subscriber error:', err);
        }
      });
    }
  }, []);

  /**
   * Open a WebSocket connection using ticket-based auth.
   * Gets a fresh single-use ticket from the REST API, then connects with ?ticket=.
   *
   * @param {object} [options] - Connection parameters
   * @param {string} [options.tenantId] - Tenant ID to subscribe to (required for system admins)
   */
  const connect = useCallback(
    async (options = {}) => {
      // Guard: already connected or connecting
      if (
        wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING
      ) {
        return;
      }

      if (!API_CONFIG.wsUrl) return;

      activeRef.current = true;
      activeOptionsRef.current = options;
      setConnectionState(WS_STATE.CONNECTING);

      // Get a fresh ticket for this connection attempt
      let ticket;
      try {
        ticket = await wsApi.getTicket();
      } catch (err) {
        console.error('[MindWS] Failed to get WebSocket ticket:', err);
        // Auth failures (401/403) — stop reconnecting, user needs to re-authenticate
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          console.warn('[MindWS] Auth failure — stopping reconnection');
          activeRef.current = false;
          reconnectAttemptRef.current = 0;
        }
        setConnectionState(WS_STATE.DISCONNECTED);
        return;
      }

      if (!ticket) {
        console.warn('[MindWS] No ticket returned — user may not be authenticated');
        activeRef.current = false;
        reconnectAttemptRef.current = 0;
        setConnectionState(WS_STATE.DISCONNECTED);
        return;
      }

      let url = `${API_CONFIG.wsUrl}?ticket=${encodeURIComponent(ticket)}`;
      if (options.tenantId) {
        url += `&tenantId=${encodeURIComponent(options.tenantId)}`;
      }
      const ws = new WebSocket(url);

      ws.onopen = () => {
        console.log('[MindWS] Connected');
        setConnectionState(WS_STATE.CONNECTED);
        reconnectAttemptRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          dispatch(message);
        } catch (err) {
          console.warn('[MindWS] Failed to parse message:', err);
        }
      };

      ws.onclose = (event) => {
        console.log(`[MindWS] Closed: code=${event.code} reason=${event.reason}`);
        wsRef.current = null;

        // Intentional disconnect (code 1000) or user disconnected — don't reconnect
        if (event.code === 1000 || !activeRef.current) {
          setConnectionState(WS_STATE.DISCONNECTED);
          return;
        }

        // Auth-related close codes — stop reconnecting, user needs to re-authenticate
        // 4401/4403: custom auth rejection codes from WS connect handler
        // 1008: policy violation (standard WebSocket close code)
        if (event.code === 4401 || event.code === 4403 || event.code === 1008) {
          console.warn(`[MindWS] Auth close code ${event.code} — stopping reconnection`);
          activeRef.current = false;
          reconnectAttemptRef.current = 0;
          setConnectionState(WS_STATE.DISCONNECTED);
          return;
        }

        // Attempt reconnection with exponential backoff
        // Each reconnect gets a fresh ticket (tickets are single-use)
        if (reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
          setConnectionState(WS_STATE.RECONNECTING);
          const delay = Math.min(
            BASE_DELAY_MS * Math.pow(2, reconnectAttemptRef.current),
            MAX_DELAY_MS
          );
          reconnectAttemptRef.current++;
          console.log(
            `[MindWS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptRef.current}/${MAX_RECONNECT_ATTEMPTS})`
          );
          reconnectTimerRef.current = setTimeout(() => {
            connect(activeOptionsRef.current);
          }, delay);
        } else {
          console.warn('[MindWS] Max reconnection attempts reached — falling back to polling');
          setConnectionState(WS_STATE.DISCONNECTED);
        }
      };

      ws.onerror = (err) => {
        console.error('[MindWS] Error:', err);
        // onclose fires after onerror — reconnection handled there
      };

      wsRef.current = ws;
    },
    [dispatch]
  );

  /**
   * Intentionally disconnect.
   */
  const disconnect = useCallback(() => {
    activeRef.current = false;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    reconnectAttemptRef.current = 0;
    if (wsRef.current) {
      wsRef.current.close(1000, 'Intentional disconnect');
      wsRef.current = null;
    }
    setConnectionState(WS_STATE.DISCONNECTED);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  const value = {
    connectionState,
    isConnected: connectionState === WS_STATE.CONNECTED,
    isReconnecting: connectionState === WS_STATE.RECONNECTING,
    lastMessage,
    connect,
    disconnect,
    subscribe,
    WS_STATE,
  };

  return <MindWebSocketContext.Provider value={value}>{children}</MindWebSocketContext.Provider>;
}

export function useMindWebSocket() {
  const context = useContext(MindWebSocketContext);
  if (!context) {
    throw new Error('useMindWebSocket must be used within a MindWebSocketProvider');
  }
  return context;
}

export default MindWebSocketContext;
